import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Radio, RadioGroup } from '@heroui/react'
import { api } from '../lib/api'
import { explain } from '../lib/staffErrors'
import { nameIn, t, type StaffLanguage } from '../lib/staffText'
import type { Outcome, Service } from '../lib/types'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import {
  CODE_RE,
  CodeField,
  cleanNames,
  LangSwitch,
  LoadingLine,
  MoveButtons,
  NamesField,
  Preview,
  ServiceScope,
  hasName,
  inOrder,
  reorder,
  useServiceName,
} from './recordParts'
import { Toggle, useFlash, useSave } from './sectionKit'
import { ChoiceSelect, IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, useOpenRecord } from './settingsKit'
import { EmptyState } from '../ui/parts'
import '../styles/orgset.css'

/**
 * Outcomes: what was done for the customer.
 *
 * The rule to know before adding the first one: a service with any outcome
 * available requires one at close. There is no separate switch. Defining an
 * outcome is the act of asking staff to record it, and an organisation that is not
 * ready to ask defines none. So the drawer for the very first outcome says what
 * saving it will start, and the desk preview beside the list shows the question
 * the clerk will meet — or that there is none.
 *
 * The alternative — a catalogue plus a "required" flag — produces a list nobody
 * fills in and a column no report can use, which is exactly the state this column
 * was in for seven phases before this screen existed.
 *
 * An outcome with no service is offered for every service. That is the common case:
 * "completed" and "documents missing" are answers to any piece of work.
 *
 * "Transferred" is drawn at the foot of the list as a fixed row: work that ended
 * because the customer was transferred records it on its own, and a person reading
 * the figures will see it beside the ones defined here.
 */

type View = 'active' | 'retired'

type Editing = { outcome: Partial<Outcome> & { name: Record<string, string> }; isNew: boolean }

/** An example code, the same in every language. */
const EXAMPLE_CODE = 'COMPLETED'

const blank = (): Editing['outcome'] => ({ code: '', name: {}, serviceId: '', sortOrder: 0, active: true })

export function Outcomes({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const [outcomes, setOutcomes] = useState<Outcome[]>([])
  const [services, setServices] = useState<Service[]>([])
  const [loaded, setLoaded] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [view, setView] = useState<View>('active')
  const serviceName = useServiceName(services)

  const load = useCallback(async () => {
    const listed = await api.outcomes(organisation)
    setOutcomes(listed.outcomes)
    setLoaded(true)
  }, [organisation])

  const { flash, toast } = useFlash()
  const { busy, error, setError, save } = useSave(load, flash)

  useEffect(() => {
    setLoaded(false)
    load().catch((err) => {
      setLoadError(explain(err, lang, 'catalogue.outcomes.notLoaded'))
      setLoaded(true)
    })
    api
      .catalogueServices(organisation)
      .then((listed) => setServices(listed.services))
      .catch(() => setServices([]))
    // The language is read for the refusal only; a change of language is not a
    // reason to read the list again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, organisation])

  const active = inOrder(outcomes.filter((o) => o.active))
  const retired = inOrder(outcomes.filter((o) => !o.active))
  useEffect(() => {
    if (retired.length === 0 && view === 'retired') setView('active')
  }, [retired.length, view])

  // The outcome open on its own page (#outcomes/<id>, #outcomes/new).
  const rec = useOpenRecord(outcomes, (o) => o.id, loaded)
  const open = (o?: Outcome) => {
    setError('')
    rec.open(o)
  }
  const editing: Editing | false | undefined = rec.isNew ? loaded && { outcome: blank(), isNew: true } : rec.row && { outcome: rec.row, isNew: false }

  // Moving one row renumbers the rows whose place changed, one save each. The
  // server keeps sortOrder per outcome and has no "move" of its own.
  const move = (id: string, by: number) =>
    void save(async () => {
      for (const row of reorder(active, id, by)) await api.updateOutcome(row.id, { sortOrder: row.sortOrder })
    }, say('record.orderSaved'))

  const nameOf = (o: Pick<Outcome, 'name' | 'code'>) => nameIn(o.name, lang, o.code)

  const shown = view === 'retired' ? retired : active
  const cols = 'minmax(220px, 1.5fr) minmax(140px, 1fr) 72px'

  if (editing) {
    return (
      <>
        {toast}
        <OutcomeDrawer
          key={rec.code}
          editing={editing}
          all={outcomes}
          services={services}
          busy={busy}
          error={error}
          onClose={rec.close}
          onSave={(given) => {
            const draft = { ...given, name: cleanNames(given.name) }
            void save(
              () =>
                editing.isNew
                  ? api.createOutcome({
                      ...draft,
                      // New outcomes go to the end of the list the desk shows.
                      sortOrder: (active.at(-1)?.sortOrder ?? 0) + 10,
                      ...(organisation ? { organisation } : {}),
                    })
                  : api.updateOutcome(draft.id!, draft),
              editing.isNew ? say('record.outcomes.added') : say('record.saved'),
            ).then((ok) => ok && rec.close())
          }}
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.outcomes')}
        line={say('settings.outcomes.line')}
        actions={
          <Button variant="primary" onPress={() => open()}>
            <Icon name="plus" size={16} /> {say('record.outcomes.add')}
          </Button>
        }
      />
      {toast}
      {loadError && <Notice className="os-gap">{loadError}</Notice>}
      {error && !editing && <Notice className="os-gap">{error}</Notice>}

      <div className="record-split">
        <div className="record-split__main">
          <ListCard<View>
            title={say('admin.section.outcomes')}
            count={
              loaded
                ? retired.length > 0
                  ? say('record.outcomes.countRetired', { n: active.length, r: retired.length })
                  : say('record.outcomes.count', { n: active.length })
                : undefined
            }
            chips={
              retired.length > 0
                ? [
                    { value: 'active', label: say('settings.rec.inUse'), count: active.length },
                    { value: 'retired', label: say('settings.rec.retired'), count: retired.length },
                  ]
                : undefined
            }
            chip={view}
            onChip={setView}
          >
            {!loaded ? (
              <LoadingLine />
            ) : view === 'active' && active.length === 0 ? (
              <EmptyState title={say('record.outcomes.none')} />
            ) : (
              <ListTable label={say('admin.section.outcomes')} cols={cols} head={[say('record.col.outcome'), say('record.offeredFor'), '']}>
                {shown.map((o, i) => (
                  <ListRow key={o.id} actions off={!o.active} onOpen={() => open(o)}>
                    <NameCell tile={<IconTile icon="check" tone={o.active ? 2 : 1} />} name={nameOf(o)} sub={o.code} />
                    <span className="rec-cell" data-one={(o.active && o.serviceId) || undefined} role="cell">
                      {serviceName(o.serviceId)}
                    </span>
                    <span role="cell">
                      {o.active && (
                        <MoveButtons
                          name={nameOf(o)}
                          first={i === 0}
                          last={i === active.length - 1}
                          disabled={busy}
                          onMove={(by) => move(o.id, by)}
                        />
                      )}
                    </span>
                  </ListRow>
                ))}
                {view === 'active' && (
                  <ListRow>
                    <NameCell
                      tile={<IconTile icon="swap" tone={1} />}
                      name={
                        <>
                          {say('record.outcomes.transferred')}{' '}
                          <Chip size="sm" variant="soft">
                            {say('record.automatic')}
                          </Chip>
                        </>
                      }
                      sub={say('record.outcomes.transferredWhen')}
                    />
                    <span className="rec-cell" role="cell">
                      {say('record.everyService')}
                    </span>
                    <span />
                  </ListRow>
                )}
              </ListTable>
            )}
          </ListCard>
        </div>

        <DeskPreview outcomes={active} services={services} />
      </div>
    </>
  )
}

/** What a clerk meets on pressing Finish, for one service, in one language. */
function DeskPreview({ outcomes, services }: { outcomes: Outcome[]; services: Service[] }) {
  const { lang: readerLang, say } = useStaffText()
  const [lang, setLang] = useState<StaffLanguage>(readerLang)
  const offered = services.filter((s) => s.active)
  const [chosen, setChosen] = useState('')
  const [picked, setPicked] = useState('')
  // Starts on a service that has an outcome of its own, when there is one: that
  // is the preview worth seeing first.
  const serviceId =
    chosen || offered.find((s) => outcomes.some((o) => o.serviceId === s.id))?.id || offered[0]?.id || ''
  const shown = outcomes.filter((o) => !o.serviceId || o.serviceId === serviceId)

  return (
    <Preview
      title={say('record.deskPreview')}
      controls={<LangSwitch lang={lang} setLang={setLang} />}
    >
      {offered.length > 0 && (
        <ChoiceSelect
          label={say('record.previewService')}
          value={serviceId}
          onChange={(id) => {
            setChosen(id)
            setPicked('')
          }}
          options={offered.map((s) => ({ value: s.id, label: nameIn(s.name, readerLang, s.code) }))}
        />
      )}
      <div className="record-desk" lang={lang}>
        {shown.length === 0 ? (
          <p className="record-desk__none">{say('record.outcomes.noQuestion')}</p>
        ) : (
          <>
            <p className="record-desk__q">{t('counter.whatWasDone', lang)}</p>
            <RadioGroup
              className="record-desk__opts"
              orientation="horizontal"
              name="record-desk-outcome"
              aria-label={t('counter.whatWasDone', lang)}
              value={picked || null}
              onChange={setPicked}
            >
              {shown.map((o) => (
                <Radio key={o.id} value={o.id}>
                  <Radio.Content>
                    <Radio.Control>
                      <Radio.Indicator />
                    </Radio.Control>
                    {nameIn(o.name, lang, o.code)}
                  </Radio.Content>
                </Radio>
              ))}
            </RadioGroup>
          </>
        )}
        <Button variant="primary" fullWidth isDisabled={shown.length > 0 && !picked}>
          {t('floor.finish', lang)}
        </Button>
      </div>
    </Preview>
  )
}

function OutcomeDrawer({
  editing,
  all,
  services,
  busy,
  error,
  onClose,
  onSave,
}: {
  editing: Editing
  all: Outcome[]
  services: Service[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (draft: Editing['outcome']) => void
}) {
  const { lang, say } = useStaffText()
  const { isNew } = editing
  const [draft, setDraft] = useState(editing.outcome)
  const code = draft.code ?? ''
  const taken = code !== '' && all.some((o) => o.id !== draft.id && o.code === code)
  const valid = CODE_RE.test(code) && !taken && hasName(draft.name)
  // Adding the first outcome is the moment the desk starts being asked: said
  // once, where it is decided, rather than as a paragraph above the list.
  const first = isNew && !all.some((o) => o.active)
  const serviceName = useServiceName(services)

  return (
    <InnerPage
      back={say('admin.section.outcomes')}
      title={isNew ? say('record.outcomes.new') : nameIn(draft.name, lang, code)}
      onClose={onClose}
      valid={valid}
      busy={busy}
      submitLabel={isNew ? say('record.outcomes.add') : say('record.save')}
      onSubmit={() => onSave(draft)}
    >
      {error && <Notice>{error}</Notice>}
      {first && (
        <p className="record-warn">
          {draft.serviceId
            ? say('record.outcomes.firstWarnOne', { service: serviceName(draft.serviceId) })
            : say('record.outcomes.firstWarn')}
        </p>
      )}
      <NamesField legend={say('record.name')} value={draft.name} onChange={(name) => setDraft({ ...draft, name })} />
      <CodeField
        value={code}
        onChange={(next) => setDraft({ ...draft, code: next })}
        taken={taken}
        isNew={isNew}
        placeholder={EXAMPLE_CODE}
      />
      <ServiceScope
        services={services}
        value={draft.serviceId ?? ''}
        onChange={(serviceId) => setDraft({ ...draft, serviceId })}
      />
      {!isNew && (
        <Toggle
          title={say('record.offeredAtDesk')}
          hint={say('record.retireHint')}
          checked={draft.active ?? true}
          onChange={(active) => setDraft({ ...draft, active })}
        />
      )}
    </InnerPage>
  )
}
